<template>
	<div class="flex w-full gap-8px">
		<div class="relative w-full">
			<div
				v-if="countryCode"
				class="typography-b2-reg absolute left-[16px] top-[27.1px]"
			>
				{{ countryCode }}
			</div>
			<input
				:type="inputTypeComputed"
				:id="uniqueId"
				:placeholder="computedPlaceholder"
				@focus="onFocus"
				@blur="onBlur"
				:value="localValue"
				@input="onInput"
				:disabled="disabled"
				:class="[inputClasses, inputPaddingClass]"
				:inputMode="inputMode"
				:name="inputTypeComputed"
				:autocomplete="autoComplete"
			/>
			<label
				class="pointer-events-none absolute left-[16px] top-0 flex h-full items-center"
				:for="uniqueId"
				id="label-fname"
			>
				<div :class="labelTextClasses">
					{{ label }}
				</div>
			</label>
			<div
				v-if="isShowTag"
				ref="slotContainer"
				class="absolute right-16px top-1/2 -translate-y-1/2 transform"
			>
				<slot name="lastused" />
			</div>
			<div
				class="absolute right-[16px] top-1/2 inline-flex -translate-y-1/2 transform"
			>
				<span
					v-if="isPassword"
					class="h-24px w-24px cursor-pointer"
					@click="togglePasswordVisibility"
				>
					<BaseIcon
						name="eye_closed"
						:class="['h-24px w-24px', { 'text-n-300': props.disabled }]"
						v-show="passwordVisible"
					/>
					<BaseIcon
						name="eye_open"
						:class="['h-24px w-24px', { 'text-n-300': props.disabled }]"
						v-show="!passwordVisible"
					/>
				</span>
				<slot name="trailing-icon" />
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { generateUUID } from '@/lib/utils'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'

const props = defineProps({
	value: {
		type: String,
		required: true,
	},
	type: {
		type: String,
		default: 'text',
	},
	placeholder: {
		type: String,
		default: '',
	},
	label: {
		type: String,
		required: true,
	},
	isError: {
		type: Boolean,
		required: true,
	},
	errorMessage: {
		type: String,
		default: '',
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	infoMessage: {
		type: String,
		default: '',
	},
	inputMode: {
		type: String,
		default: '',
	},
	countryCode: {
		type: String,
		default: '',
	},
	isLazy: {
		type: Boolean,
		default: true,
	},
	isLogin: {
		type: Boolean,
		default: false,
	},
	autoComplete: {
		type: String,
		default: 'on',
	},
})

const emit = defineEmits(['input'])

const localValue = ref(props.value)
const passwordVisible = ref(false)
const uniqueId = ref(`input-${generateUUID()}`)
const isFocused = ref(false)
const isFirstFocus = ref(false)
const slotWidth = ref(0)
const slotContainer = ref<HTMLElement | null>(null)

const isPassword = computed(() => props.type === 'password')
const isShowTag = computed(
	() =>
		props.isLogin && !isFocused.value && !isFirstFocus.value && !props.value,
)
const inputTypeComputed = computed(() =>
	isPassword.value && passwordVisible.value ? 'text' : props.type,
)
const computedPlaceholder = computed(() =>
	isFocused.value && !localValue.value ? props.placeholder : '',
)

const inputClasses = computed(() => [
	'input-field-floating__input',
	{
		'input-field-floating__input--disabled': props.disabled,
		'input-field-floating__input--error': props.isError && !props.disabled,
		'input-field-floating__input--with-icon': isPassword.value,
		'input-field-floating__input--active':
			isFocused.value || localValue.value || props.countryCode,
		'input-field-floating__input--with-tag': tagStyle,
	},
])

const inputPaddingClass = computed(() => {
	if (props.countryCode) {
		const { length } = props.countryCode
		return `input-field-floating__pl-${length}`
	}
	return ''
})

const labelTextClasses = computed(() => [
	'input-field-floating__label-text',
	{
		'input-field-floating__label-text--disabled': props.disabled,
		'input-field-floating__label-text--error':
			props.isError && !props.disabled && (isFocused.value || localValue.value),
		'input-field-floating__label-text--active':
			isFocused.value || localValue.value || props.countryCode,
	},
])

const onFocus = () => {
	if (!props.disabled) {
		isFocused.value = true
		isFirstFocus.value = true
	}
}

const onBlur = (event: any) => {
	if (!props.disabled) {
		isFocused.value = false
	}

	if (props.isLazy) {
		updateInput(event)
	}
}

const onInput = (event: any) => {
	if (!props.disabled && !props.isLazy) {
		updateInput(event)
	}
}

function updateInput(event: any) {
	localValue.value = event.target.value
	emit('input', localValue.value)
}

const togglePasswordVisibility = () => {
	if (!props.disabled) {
		passwordVisible.value = !passwordVisible.value
	}
}

const updateSlotWidth = () => {
	if (slotContainer.value) {
		slotWidth.value = slotContainer.value.clientWidth
	}
}

const tagStyle = computed(
	() =>
		isShowTag.value && {
			paddingRight: `${slotWidth.value}px`,
			textOverflow: 'ellipsis',
		},
)

watch(
	() => props.value,
	newVal => {
		localValue.value = newVal
	},
)

onMounted(() => {
	updateSlotWidth()
})
</script>

<style></style>
